/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import React, { useState } from 'react';
import {
	Checkbox,
	FormControlLabel,
	FormGroup,
	FormLabel,
	MenuItem,
	Radio,
	RadioGroup,
	TextField
} from '@mui/material';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../actions/actions';
import { FormattedMessage, injectIntl } from 'react-intl';
import SettingsPaper from '../utils/SettingsPaper';
import { StreamSheetAccordion } from '../utils/StreamSheetAccordion';
import { graphManager } from '../../GraphManager';
import { withAppData } from '../app/AppProvider';
import UploadButton from '../utils/UploadButton';
import { rename } from './AppGraphQL';
import { useLocalState } from './GraphQLWSClient';

const styleFlags = {
	width: '21px',
	height: '13px',
	marginTop: '5px',
	marginRight: '10px'
};

const DEF_INTERVALS = [10, /* 20, 50, */ 100, /* 200, */ 500, 1000];
const isCustom = (interval) => !DEF_INTERVALS.includes(interval);
const getCustomInterval = (customInterval, defInterval = 'custom', currInterval = 2000) => {
	if (customInterval == null) return defInterval === 'custom' ? Number((currInterval / 1000).toFixed(3)) : 2;
	return customInterval;
};

function AppSettingsComponent(props) {
	const [name, setName] = useState(props.name);
	const [description, setDescription] = useState(props.settings.description);
	const [expanded, setExpanded] = useState();
	const [nameError, setNameError] = useState();
	const [categories, setLocalCategories] = useLocalState(props.settings.categories || []);
	const [imageFileName, setImageFileName] = useState(props.settings.image ? 'image.svg' : '');
	const [tourFileName, setTourFileName] = useState(props.settings.tour && props.settings.tour.name ? props.settings.tour.name : '');
	const [customInterval, setCustomInterval] = useState();
	const defaultInterval = isCustom(props.settings.defaultInterval) ? 'custom' : props.settings.defaultInterval;

	const updateSettings = (settings) => {
		props.updateMachineSettings(props.machineId, settings);
		props.updateAppSettings(settings);
	};

	const handleClose = () => {
		props.close();
	};

	const handleMachineLocaleSelect = (event) => {
		const locale = event.target.value;
		updateSettings({ locale });
	};

	const handleChange = (exp) => {
		setExpanded(exp === expanded ? undefined : exp);
	};

	const isNameValid = (newName) => newName.length > 0;

	const validateName = (newName) => {
		if (newName === props.settings.name) {
			setNameError(undefined);
			return true;
		}

		if (!newName || newName.length < 1) {
			setNameError(
				props.intl.formatMessage({
					id: 'Error.EmptyName',
					defaultMessage: ''
				})
			);
			return false;
		}
		if (!isNameValid(newName)) {
			setNameError(
				props.intl.formatMessage({
					id: 'Error.InvalidName',
					defaultMessage: ''
				})
			);
			return false;
		}

		setNameError(undefined);

		return true;
	};

	const onNameChange = (event) => {
		setName(event.target.value);
		validateName(event.target.value);
	};

	const onNameBlur = async () => {
		if (validateName(name)) {
			const result = await rename(props.machineId, name);
			if (result === 'CONFLICT') {
				setNameError(
					props.intl.formatMessage({
						id: 'Error.DuplicateName',
						defaultMessage: ''
					})
				);
			}
		}
	};

	const onDescriptionChange = (event) => {
		setDescription(event.target.value);
	};

	const onDescriptionBlur = (event) => {
		props.updateMachineSettings(props.machineId, { description: event.target.value });
	};

	const onCategoriesChange = (event) => {
		setLocalCategories(event.target.value.split(','));
	};

	const onCategoriesBlur = (/*event*/) => {
		updateSettings({ categories });
		setLocalCategories(null);
	};

	const onImageChange = (info) => {
		setImageFileName(info.name || '');
		updateSettings({ image: info.content|| '' });
	};

	const onTourChange = (info) => {
		const json = JSON.parse(info.content);
		if (json.name) {
			setTourFileName(json.name || '');
			updateSettings({tour: info.content ? json : {}});
		}
	};

	const handleDefaultIntervalChange = (event) => {
		const value = event.target.value;
		const custom = value === 'custom';
		const interval = custom ? (customInterval || 2) * 1000 : Number(value);
		updateSettings({ defaultInterval: interval });
	};
	const onCustomIntervalChange = (event) => {
		let interval = event.target.value;
		if (interval) {
			interval = Number(interval);
			if (interval !== 0) interval = Math.max(0.001, interval);
		}
		setCustomInterval(interval);
	};
	const onCustomIntervalBlur = (event) => {
		const interval = Math.max(0.001, Number(event.target.value) || 2);
		const msInterval = interval * 1000;
		setCustomInterval();
		updateSettings({ defaultInterval: msInterval });
	};

	if (!props.open) {
		return null;
	}

	const container = graphManager.machineContainer;
	if (!container) {
		return null;
	}

	const handleShowOutbox = (event, state) => {
		container.outboxVisible = !container.outboxVisible;
		updateSettings({ showOutbox: state });
	};

	return (
		<SettingsPaper
			open={props.open}
			close={handleClose}
			helpContext="appsettings"
			title={<FormattedMessage id="AppSettings.Title" defaultMessage="App Settings" />}
		>
			<TextField
				variant="outlined"
				size="small"
				margin="normal"
				label={<FormattedMessage id="AppSettings.AppName" defaultMessage="Name" />}
				fullWidth
				value={name}
				error={typeof nameError === 'string'}
				helperText={nameError}
				onChange={onNameChange}
				onBlur={onNameBlur}
			/>
			<TextField
				variant="outlined"
				size="small"
				margin="normal"
				label={<FormattedMessage id="AppSettings.AppDescription" defaultMessage="Description" />}
				fullWidth
				multiline
				value={description}
				onChange={onDescriptionChange}
				onBlur={onDescriptionBlur}
			/>
			<FormControlLabel
				sx={{ mt: '10px', mb: '15px' }}
				control={<Checkbox checked={container.outboxVisible} onChange={handleShowOutbox} />}
				label={<FormattedMessage id="AppSettings.ShowOutbox" defaultMessage="Show Outbox" />}
			/>
			<StreamSheetAccordion
				id="panelCycle"
				expanded={expanded === 'panelCycle'}
				onChange={() => handleChange('panelCycle')}
				title={<FormattedMessage id="AppSettings.Update" defaultMessage="Update Default Interval" />}
				details={<FormattedMessage id="AppSettings.Delay" defaultMessage="Delay" />}
			>
				<RadioGroup name="type" value={defaultInterval} onChange={handleDefaultIntervalChange}>
					<FormLabel
						style={{
							marginTop: '7px',
							marginBottom: '7px'
						}}
					>
						<FormattedMessage id="AppSettings.DefaultInterval" defaultMessage="Default Interval" />
					</FormLabel>
					<FormControlLabel value={10} control={<Radio />} label="10 ms" />
					{/* <FormControlLabel value={20} control={<Radio />} label="20 ms" />
					<FormControlLabel value={50} control={<Radio />} label="50 ms" /> */}
					<FormControlLabel value={100} control={<Radio />} label="100 ms" />
					{/* <FormControlLabel value={200} control={<Radio />} label="200 ms" /> */}
					<FormControlLabel value={500} control={<Radio />} label="500 ms" />
					<FormControlLabel
						value={1000}
						control={<Radio />}
						label={<FormattedMessage id="AppSettings.OneSec" defaultMessage="1 second" />}
					/>
					<FormControlLabel
						value="custom"
						control={<Radio />}
						label={<FormattedMessage id="AppSettings.Custom" defaultMessage="Custom" />}
					/>
				</RadioGroup>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					style={{ marginLeft: '20px' }}
					// style={{ marginLeft: spacing(1) }}
					type="number"
					disabled={defaultInterval !== 'custom'}
					label={<FormattedMessage id="AppSettings.CustomInterval" defaultMessage="Interval in seconds" />}
					// fullWidth
					value={getCustomInterval(customInterval, defaultInterval, props.settings.defaultInterval)}
					onChange={onCustomIntervalChange}
					onBlur={onCustomIntervalBlur}
				/>
			</StreamSheetAccordion>
			<StreamSheetAccordion
				id="panelTemplate"
				expanded={expanded === 'panelTemplate'}
				onChange={() => handleChange('panelTemplate')}
				title={<FormattedMessage id="AppSettings.Template" defaultMessage="Template" />}
				details={<FormattedMessage id="AppSettings.TemplateDetail" defaultMessage="Template" />}
			>
				<FormGroup>
					<TextField
						variant="outlined"
						size="small"
						margin="normal"
						label={<FormattedMessage id="AppSettings.AppCategories" defaultMessage="Categories" />}
						fullWidth
						multiline
						value={categories.join(',')}
						onChange={onCategoriesChange}
						onBlur={onCategoriesBlur}
					/>
					<UploadButton
						label={<FormattedMessage id="AppSettings.Image" defaultMessage="Image" />}
						file={imageFileName}
						onChange={onImageChange}
					/>
					<UploadButton
						label={<FormattedMessage id="AppSettings.Tour" defaultMessage="Tour" />}
						file={tourFileName}
						onChange={onTourChange}
					/>
				</FormGroup>
			</StreamSheetAccordion>
			<StreamSheetAccordion
				id="panelOther"
				expanded={expanded === 'panelOther'}
				onChange={() => handleChange('panelOther')}
				title={<FormattedMessage id="AppSettings.Language" defaultMessage="Language" />}
				details={<FormattedMessage id="AppSettings.LanguageDetail" defaultMessage="Language" />}
			>
				<FormGroup>
					<TextField
						label={<FormattedMessage id="AppSettings.MachineLanguage" defaultMessage="Language" />}
						variant="outlined"
						size="small"
						select
						value={props.settings.locale}
						onChange={handleMachineLocaleSelect}
					>
						<MenuItem key="en" value="en">
							<img alt="EN" src="images/flags/gb.svg" style={styleFlags} />
							English (UK)
						</MenuItem>
						<MenuItem key="de" value="de">
							<img alt="DE" src="images/flags/de.svg" style={styleFlags} />
							German
						</MenuItem>
						<MenuItem key="us" value="us">
							<img alt="EN-US" src="images/flags/us.svg" style={styleFlags} />
							English (US)
						</MenuItem>
					</TextField>
				</FormGroup>
			</StreamSheetAccordion>
		</SettingsPaper>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export const AppSettings = withAppData(({ machine, dispatchAppData }) => ({
	categories: machine.categories,
	machineId: machine.id,
	settings: machine.settings,
	updateAppSettings: (settings) => dispatchAppData({ type: 'update-machine-settings', data: settings })
}))(injectIntl(connect(null, mapDispatchToProps)(AppSettingsComponent)));
